"use client";

import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from "react";

export type ModalSignalType = "CLEAR_SELECTION" | "CHANGE_GROUPING";

interface ModalSignalContextType {
  pendingSignal: ModalSignalType | null;
  sendSignal: (signal: ModalSignalType, payload?: unknown) => void;
  consumeSignal: <T = unknown>(signal: ModalSignalType) => { consumed: boolean; payload?: T };
}

const ModalSignalContext = createContext<ModalSignalContextType | null>(null);

interface ModalSignalProviderProps {
  children: ReactNode;
}

export function ModalSignalProvider({ children }: ModalSignalProviderProps) {
  const [pendingSignal, setPendingSignal] = useState<ModalSignalType | null>(null);
  const pendingSignalRef = useRef<ModalSignalType | null>(null);
  const payloadRef = useRef<unknown>(null);

  const sendSignal = useCallback((signal: ModalSignalType, payload?: unknown) => {
    pendingSignalRef.current = signal;
    payloadRef.current = payload ?? null;
    setPendingSignal(signal);
  }, []);

  const consumeSignal = useCallback(<T = unknown>(signal: ModalSignalType): { consumed: boolean; payload?: T } => {
    if (pendingSignalRef.current === signal) {
      const payload = payloadRef.current as T;
      pendingSignalRef.current = null;
      payloadRef.current = null;
      setPendingSignal(null);
      return { consumed: true, payload };
    }
    return { consumed: false };
  }, []);

  return (
    <ModalSignalContext.Provider value={{ pendingSignal, sendSignal, consumeSignal }}>
      {children}
    </ModalSignalContext.Provider>
  );
}

export function useModalSignal() {
  const context = useContext(ModalSignalContext);
  if (!context) {
    throw new Error("useModalSignal must be used within ModalSignalProvider");
  }
  return context;
}
